import './../Select/select.css';
import './combobox.css';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { isEmpty } from 'radash';
import { forwardRef, ReactNode, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Select, { components, GroupBase, MenuListProps, OptionProps, Props, SingleValueProps } from 'react-select';
import type {} from 'react-select/base';
import CreatableSelect from 'react-select/creatable';
import { Virtuoso } from 'react-virtuoso';

import { isProgrammaticCellScroll } from '@/components/datamodel/hooks/useScrollCellIntoView';
import { useTabNavigation } from '@/hooks/useTabNavigation';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { Loader as CoreLoader } from '../Loader';

// This ensures that Emotion's styles are inserted before Tailwind's styles so that Tailwind classes have precedence over Emotion
const EmotionCacheProvider = ({ children }: { children: React.ReactNode }) => {
    const cache = useMemo(
        () =>
            createCache({
                key: 'react-select-styles',
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                insertionPoint: document.querySelector('title')!,
            }),
        []
    );

    return <CacheProvider value={cache}>{children}</CacheProvider>;
};

declare module 'react-select/base' {
    export interface Props<
        Option,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        IsMulti extends boolean,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Group extends GroupBase<Option>
    > {
        visualizationHeight?: string;
        creatable?: boolean,
        borderless?: boolean,
        inputControlClassnames?: string,
        hideDropdownIndicator?: boolean,
        gridMode?: boolean,
        /**
         * Bypasses the Virtuoso-based `MenuList` so options render into
         * plain block flow. Enabled for browse-list dropdowns where the
         * menu is sized to its content (`width: max-content`) - Virtuoso
         * wraps items in a fixed-width viewport whose intrinsic max-content
         * is 0, so keeping virtualization on collapses the whole menu back
         * to the trigger width. Only used with `fitMenuToContent`.
         */
        fitMenuToContent?: boolean,
    }
}

function Loader() {
    return <CoreLoader />;
}

// Custom SingleValue component that enables text selection / copy
function SelectableSingleValue<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: SingleValueProps<Option, IsMulti, Group>
) {
    // Check if the select is disabled/readonly
    const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Select all text on double-click
        e.stopPropagation();
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(e.currentTarget);
        selection?.removeAllRanges();
        selection?.addRange(range);
    };

    return (
        <div
            {...props.innerProps}
            style={{
                userSelect: 'text',
                cursor: 'text',
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                flex: '2 1 80%',
                minWidth: 0,
                overflow: 'hidden',
            }}
            onDoubleClick={handleDoubleClick}
        >
            {props.children}
        </div>
    );
}

function MenuList<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>({ selectProps, children }: MenuListProps<Option, IsMulti, Group>) {
    const maxHeight = parseInt(selectProps.visualizationHeight?.toString() || '224', 10);
    const childArray = children as ReactNode[];
    const optionHeight = 40;
    const calculatedHeight = Math.min(childArray.length * optionHeight, maxHeight);

    // Browse-list dropdowns are sized with `width: max-content` so the
    // menu can grow to the widest option. Virtuoso wraps items in a
    // scroll viewport whose intrinsic max-content width is 0, so leaving
    // virtualization on would collapse the outer menu back to the tiny
    // trigger width. Fall back to a plain scrollable list here - browse
    // enums are typically small enough that we don't need virtualization,
    // and this is the only place we ever render into a `max-content`
    // parent.
    if (selectProps.fitMenuToContent && Array.isArray(children) && children.length > 0) {
        return (
            <div
                style={{
                    maxHeight,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
            >
                {children}
            </div>
        );
    }

    return (
        Array.isArray(children) && children.length > 0
            ? <Virtuoso
                style={{ height: calculatedHeight }}
                totalCount={childArray.length}
                itemContent={(index) => <div>{childArray[index]}</div>}
            />
            : <div>{children}</div>
    );
}

function SafeOption(props: OptionProps<any, any, any>) {
    const handled = useRef(false);
    return (
        <components.Option
            {...props}
            innerProps={{
                ...props.innerProps,
                onMouseDown: (e) => {
                    props.innerProps.onMouseDown?.(e);
                    if (!props.isDisabled) {
                        handled.current = true;
                        props.selectOption(props.data);
                    }
                },
                onClick: (e) => {
                    if (handled.current) { handled.current = false; return; }
                    props.innerProps.onClick?.(e);
                },
            }}
        />
    );
}

interface ComboboxRef {
    focus: () => void;
    blur: () => void;
}

const Combobox = forwardRef<ComboboxRef, Props<any, boolean, GroupBase<any>>>(
    (
        (
            {
                className,
                components,
                creatable = false,
                borderless = false,
                visualizationHeight = '224px',
                inputControlClassnames = '',
                gridMode = false,
                ...props
            }: Props<any, boolean, GroupBase<any>>,
            ref
        ) => {
            const { t } = useTranslation();
            const SelectWrapper = creatable ? CreatableSelect : Select;

            const selectRef = useRef<any>(null);
            const containerRef = useRef<HTMLDivElement>(null);
            const [isMenuOpen, setIsMenuOpen] = useState(false);
            const [flipHorizontally, setFlipHorizontally] = useState(false);
            const [menuMaxWidth, setMenuMaxWidth] = useState<number | undefined>(undefined);
            // True when this Combobox lives inside a browse-items-list
            // editing cell (marked via `data-editing-cell="true"` in
            // EditableItemCell). Those cells are typically narrower than
            // their option labels, so we let the dropdown size to its
            // content instead of matching the tiny trigger. Other
            // Comboboxes (Collection tables in the item editor, dialog
            // pickers, etc.) keep their default trigger-matched width.
            const [fitMenuToContent, setFitMenuToContent] = useState(false);
            useLayoutEffect(() => {
                if (!containerRef.current) return;
                setFitMenuToContent(
                    Boolean(
                        containerRef.current.closest('[data-editing-cell="true"]'),
                    ),
                );
            }, []);

            const getClippingParent = (element: HTMLElement | null): HTMLElement | null => {
                if (!element) return null;
                let parent = element.parentElement;
                while (parent) {
                    const style = getComputedStyle(parent);
                    const overflow = style.overflow + style.overflowX + style.overflowY;
                    if (/(auto|scroll|hidden)/.test(overflow)) {
                        return parent;
                    }
                    parent = parent.parentElement;
                }
                return null;
            };

            const handleTabNavigation = useTabNavigation();

            useImperativeHandle(ref, () => ({
                focus: () => {
                    if (selectRef.current) {
                        selectRef.current.focus();
                    }
                },
                blur: () => {
                    if (selectRef.current) {
                        selectRef.current.blur();
                    }
                }
            }));

            return (
                <EmotionCacheProvider>
                    <div
                        ref={containerRef}
                        className={cn('tw-w-full', gridMode && 'tw-min-w-0')}
                    >
                        <SelectWrapper
                            ref={selectRef}
                            closeMenuOnScroll={(e) => {
                                // Ignore the scroll-into-view that AttributeGridCell
                                // runs on focus/click. That event fires on mouse-up
                                // and would otherwise close a menu that just opened
                                // on mouse-down (LAX-10244).
                                if (isProgrammaticCellScroll()) {
                                    return false;
                                }
                                const { target } = e;
                                if (!(target instanceof Element)) {
                                    return false;
                                }
                                return !(target as Element).closest('.select-content');
                            }}
                            className={cn(
                                `tw-flex
                                tw-w-full
                                tw-bg-transparent
                                lg-font-dynamic
                                tw-min-h-9
                                tw-ring-offset-background
                                disabled:tw-cursor-not-allowed
                                placeholder:tw-text-t-inactive
                                placeholder:lg-font-dynamic`,
                                className
                            )}
                            classNames={{
                                control: ({ isFocused, isDisabled }) =>
                                    `select-trigger tw-min-h-[36px] ${isFocused ? 'tw-border-primary-focus !tw-border-2 !tw-border-solid ' : ''}
                                    focus:!tw-shadow-none focus-visible:tw-outline-none ${inputControlClassnames}
                                    ${borderless || gridMode ? 'borderless-select-trigger' : 'bordered-select-trigger'}
                                    ${isDisabled && !gridMode ? 'tw-cursor-not-allowed !tw-bg-fill-secondary !tw-border-solid !tw-border !tw-border-primary' : ''}
                                    ${gridMode && !isFocused ? 'grid-row-highlightable-combobox' : ''}
                                `,
                                option: ({ isSelected }) =>
                                    `tw-text-black select-item ${isSelected ? 'select-item-selected' : ''}`,
                                menu: () => cn(
                                    'select-content',
                                    gridMode && 'select-content--in-table',
                                    fitMenuToContent && 'select-content--fit-content',
                                ),
                                menuPortal: () => 'tw-z-[1060]',
                                valueContainer: () => 'tw-px-1 tw-w-full',
                                input: ({ isDisabled }) => (isDisabled && !gridMode ? 'tw-bg-fill-secondary': ''),
                                multiValue:  ({ isDisabled }) => isDisabled
                                    ? gridMode
                                        ? 'tw-text-t-inactive'
                                        : 'tw-bg-fill-secondary !tw-border-solid !tw-border !tw-border-primary !tw-rounded-sm tw-text-t-inactive'
                                    : '',
                                multiValueRemove: ({ isDisabled }) => (isDisabled && !gridMode ? 'tw-invisible' : ''),
                            }}
                            {...props}
                            menuPlacement="auto"
                            menuPosition="absolute"
                            menuPortalTarget={document.body}
                            styles={{
                                container: (base) => ({
                                    ...base,
                                    width: '100%',
                                    ...(gridMode ? { minWidth: 0 } : {}),
                                }),
                                menu: (base) => ({
                                    ...base,
                                    scrollbarGutter: 'stable',
                                    left: flipHorizontally ? 'auto' : 0,
                                    right: flipHorizontally ? 0 : 'auto',
                                    ...(menuMaxWidth !== undefined && { maxWidth: menuMaxWidth }),
                                    // Browse-list dropdowns break out of
                                    // their narrow cell and grow to the
                                    // longest option, capped only by the
                                    // viewport (`menuMaxWidth` above).
                                    // The min-width keeps the dropdown at
                                    // least as wide as the trigger for
                                    // enums whose options happen to be
                                    // shorter than the column.
                                    ...(fitMenuToContent && {
                                        width: 'max-content',
                                        minWidth: '100%',
                                    }),
                                }),
                                menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 1060,
                                }),
                                dropdownIndicator: (provided) => ({
                                    ...provided,
                                    display: isEmpty(props.options) || gridMode ? 'none': 'flex',
                                    color: (props.isDisabled) ? 'var(--gray-350)': provided.color,
                                    alignItems: 'center',
                                }),
                                indicatorSeparator: (provided, state) => ({
                                    ...provided,
                                    display:  isEmpty(props.options) || gridMode  ? 'none' :'block',
                                    borderLeft: `1px solid ${
                                        state.isDisabled ? 'var(--gray-300)' : provided.borderLeftColor || provided.borderColor
                                    }`,                                }),
                                control: (provided, state) => ({
                                    ...provided,
                                    width: '100%',
                                    minHeight: 36,
                                    backgroundColor: state.isDisabled && !gridMode
                                        ? 'var(--gray-100)'
                                        : provided.backgroundColor,
                                    borderColor: state.isDisabled && !gridMode
                                        ? 'var(--gray-300)'
                                        : provided.borderColor,
                                    boxShadow: 'none',
                                    cursor: state.isDisabled ? 'not-allowed' : 'text',
                                }),
                                valueContainer: (provided, state) => ({
                                    ...provided,
                                    minHeight: 36,
                                    padding: 0,
                                    paddingBottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    // Inline styles need CSS values; tw-text-t-inactive => var(--gray-350)
                                    color: state.isDisabled ? 'var(--gray-350)' : provided.color,
                                }),
                                singleValue: (provided, state) => ({
                                    ...provided,
                                    margin: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%',
                                    flex: '2 1 80%',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    // Inline styles need CSS values; tw-text-t-inactive => var(--gray-350)
                                    color: state.isDisabled ? 'var(--gray-350)' : provided.color,
                                }),
                                placeholder: (provided) => ({
                                    ...provided,
                                    margin: 0,
                                    padding: 0,
                                    display: 'block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%',
                                    width: '0%',
                                }),
                                input: (provided, state) => {
                                    const hasValue = state.hasValue;
                                    // Check if menu is open via selectProps
                                    const menuIsOpen = (state.selectProps as any)?.menuIsOpen || false;
                                    // Check if input has a value (user is typing)
                                    const inputValue = (state.selectProps as any)?.inputValue || '';
                                    // Hide when there's a selected value, menu is closed, and input is empty
                                    const shouldHide = hasValue && !menuIsOpen && !inputValue;
                                    return {
                                        ...provided,
                                        margin: 0,
                                        padding: 0,
                                        flex: shouldHide ? '0 0 0px' : '1 1 20%',
                                        minWidth: 0,
                                        zIndex: 1,
                                        width: shouldHide ? 0 : 'auto',
                                        opacity: shouldHide ? 0 : 1,
                                        pointerEvents: shouldHide ? 'none' : 'auto',
                                    };
                                },
                                indicatorsContainer: (provided) => ({
                                    ...provided,
                                    minHeight: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                }),
                            }}
                            components={{
                                ...components,
                                MenuList,
                                Option: SafeOption,
                                LoadingIndicator: Loader,
                                SingleValue: SelectableSingleValue,
                            }}
                            visualizationHeight={visualizationHeight}
                            creatable={creatable}
                            fitMenuToContent={fitMenuToContent}
                            formatCreateLabel={(inputValue) => (
                                <p>{t('CREATE_NEW_OPTION')} &apos;{inputValue}&apos;</p>
                            )}
                            onMenuOpen={() => {
                                setIsMenuOpen(true);
                                requestAnimationFrame(() => {
                                    const menu = document.querySelector('.select-content') as HTMLElement;
                                    const control = containerRef.current?.querySelector('.select-trigger') as HTMLElement | null;
                                    if (!menu || !control) return;

                                    const controlRect = control.getBoundingClientRect();
                                    // Browse-cell dropdowns intentionally
                                    // ignore the local <td>/<tr> clipping
                                    // parent so they can grow to their
                                    // longest option; we still keep them
                                    // inside the viewport. `menu.scrollWidth`
                                    // reflects the natural content width
                                    // now that `width: max-content` is on
                                    // the menu (see `styles.menu`).
                                    const clippingParent = fitMenuToContent
                                        ? null
                                        : getClippingParent(control);
                                    const bounds = clippingParent
                                        ? clippingParent.getBoundingClientRect()
                                        : { left: 0, right: window.innerWidth };
                                    const naturalWidth = fitMenuToContent
                                        ? menu.scrollWidth
                                        : menu.getBoundingClientRect().width;

                                    const spaceOnRight = bounds.right - controlRect.left;
                                    const spaceOnLeft = controlRect.right - bounds.left;
                                    const overflowsRight =
                                        controlRect.left + naturalWidth > bounds.right;

                                    if (overflowsRight && spaceOnLeft >= spaceOnRight) {
                                        setFlipHorizontally(true);
                                        setMenuMaxWidth(Math.min(naturalWidth, spaceOnLeft));
                                    } else {
                                        setFlipHorizontally(false);
                                        setMenuMaxWidth(Math.min(naturalWidth, spaceOnRight));
                                    }
                                });
                                props.onMenuOpen?.();
                            }}
                            onMenuClose={() => {
                                setIsMenuOpen(false);
                                setFlipHorizontally(false);
                                setMenuMaxWidth(undefined);
                                props.onMenuClose?.();
                            }}
                            onKeyDown={(e) => {
                                props.onKeyDown?.(e);
                                if (e.defaultPrevented) {
                                    return;
                                }
                                if (e.key === 'Tab' && isMenuOpen) {
                                    const instance = selectRef.current;
                                    const focusedOption = instance?.state?.focusedOption;

                                    if (focusedOption) {
                                        e.preventDefault();

                                        if (typeof instance?.selectOption === 'function') {
                                            instance.selectOption(focusedOption);
                                        } else if (typeof instance?.setValue === 'function') {
                                            instance.setValue(focusedOption, 'select-option');
                                        }
                                    }
                                }

                                handleTabNavigation?.(e);
                            }}
                        />
                    </div>
                </EmotionCacheProvider>
            );
        }
    ));

Combobox.displayName = 'Combobox';

export {Combobox};
